import type { SxProps } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';

import useSWR from 'swr';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router';

import { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { TxDataGrid } from 'src/auditoria/components/tx-data-grid';
import {
  DsNum,
  DsButton,
  GradeCanvas,
  DsPageHeader,
  DsStatusChip,
  SX_GRADE_CANVAS,
} from 'src/auditoria/components/ds';

import { formatDateBr, formatCurrency } from '../Nfse/types';

// ----------------------------------------------------------------------
// Parcelamento → parcelas de um acordo. Backend: GET /portal-api/parcelamento/:numero.
//
// Não tem artboard próprio: é o destino do "Ver parcelas" de `portal-parcelamento`.
// Migrada junto para a lista não cair numa tela de outro vocabulário — cabeçalho,
// casulo de grade, chip e número do kit.
// ----------------------------------------------------------------------

// O `TxDataGrid` do portal declara `sx?: SxProps` (tema default do MUI) enquanto o
// kit tipa `SxProps<Theme>` do tema do app. Mesma forma, tipos nominalmente
// distintos: o estreitamento acontece uma vez aqui, sem `any` e sem `@ts-ignore`.
const { sx: SX_GRADE_KIT, ...PROPS_GRADE } = SX_GRADE_CANVAS;
const SX_GRADE = SX_GRADE_KIT as SxProps;

interface Parcela {
  parcela: number;
  receita: string | null;
  sigla: string | null;
  vencimento: string | null;
  situacao: number | null;
  cancelada: boolean;
  valor: number;
  juros: number;
  multa: number;
  nosso_numero: string | null;
  codigo_barras: string | null;
}

interface ParcelasResponse {
  numero: number;
  parcelas: Parcela[];
}

async function copiar(texto: string) {
  try {
    await navigator.clipboard.writeText(texto);
    toast.success('Código de barras copiado.');
  } catch {
    toast.error('Não foi possível copiar.');
  }
}

export default function ParcelamentoDetailPage() {
  const { numero = '' } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useSWR<ParcelasResponse>(
    numero ? `/portal-api/parcelamento/${numero}` : null,
    fetcher
  );

  const parcelas = data?.parcelas ?? [];

  const columns: GridColDef<Parcela>[] = [
    {
      field: 'parcela',
      headerName: 'Parcela',
      width: 100,
      renderCell: (params) => <DsNum mono>{params.row.parcela}</DsNum>,
    },
    { field: 'receita', headerName: 'Receita', flex: 1, minWidth: 220 },
    {
      field: 'vencimento',
      headerName: 'Vencimento',
      width: 130,
      renderCell: (params) => <DsNum mono>{formatDateBr(params.row.vencimento)}</DsNum>,
    },
    {
      field: 'valor',
      headerName: 'Valor (R$)',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <DsNum mono align="right" sx={{ fontWeight: 700 }}>
          {formatCurrency(params.row.valor)}
        </DsNum>
      ),
    },
    {
      field: 'situacao',
      headerName: 'Situação',
      width: 130,
      sortable: false,
      // O enum de situação da parcela não tem dicionário publicado; só o
      // cancelamento é um booleano confiável. Mantida a semântica anterior.
      renderCell: (params) =>
        params.row.cancelada ? (
          <DsStatusChip tone="error">Cancelada</DsStatusChip>
        ) : (
          <DsStatusChip>Situação {params.row.situacao ?? '—'}</DsStatusChip>
        ),
    },
    {
      field: 'acoes',
      headerName: '',
      width: 190,
      sortable: false,
      filterable: false,
      align: 'right',
      renderCell: (params) =>
        params.row.codigo_barras ? (
          <DsButton
            tone="neutral"
            sx={{ padding: '6px 12px', fontSize: 12 }}
            onClick={() => copiar(params.row.codigo_barras as string)}
          >
            Copiar código de barras
          </DsButton>
        ) : null,
    },
  ];

  return (
    <DashboardContent>
      <DsPageHeader
        weight={800}
        title={`Parcelamento ${numero}`}
        subtitle="Parcelas do acordo · vencimento, valor e código de barras."
        action={
          <DsButton tone="neutral" size="tall" onClick={() => navigate('/parcelamento')}>
            Voltar aos acordos
          </DsButton>
        }
      />

      <GradeCanvas>
        <TxDataGrid<Parcela>
          rows={parcelas}
          columns={columns}
          loading={isLoading}
          emptyLabel="Nenhuma parcela encontrada"
          getRowId={(row) => row.parcela}
          initialPageSize={100}
          hideFooter
          {...PROPS_GRADE}
          sx={SX_GRADE}
        />
      </GradeCanvas>
    </DashboardContent>
  );
}
